// ─── Identity ─────────────────────────────────────────────────────────────────
export const PROFILE = {
  name:     "Nisarg Patel",
  initials: "NP",
  role:     "Software Engineer",
  bio:      "Software engineer at Manulife building Java microservices and Angular front-ends for policy, claims, and payments. I care about code that stays readable a year later.",
  location: "Toronto, Canada",
  langs:    "English, Gujarati, Hindi",
  email:    "ngp0330@gmail.com",
  github:   "https://github.com/nisarg0330",
  linkedin: "https://linkedin.com/in/nisarg0330",
  seeking:  "Software Engineer @ Manulife",
};

// Shared scroll helper — lives here so no component has to import from App (cycle).
export const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  // Skipping several sections would otherwise land you mid-fade. Settle the
  // destination first so it is already painted when the scroll arrives.
  el.querySelectorAll(".reveal").forEach((n) => n.classList.add("in"));
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ─── Themes ───────────────────────────────────────────────────────────────────
// id must match a :root[data-theme="…"] block in globalStyles.js.
export const THEMES = [
  { id: "dark",     label: "Dark+" },
  { id: "light",    label: "Light+" },
  { id: "monokai",  label: "Monokai" },
  { id: "contrast", label: "High Contrast" },
];

// ─── Tabs / Outline ───────────────────────────────────────────────────────────
// id must match the section element id; label is the "filename" in the tab bar.
export const SECTIONS = [
  { id: "hero",     label: "index.jsx",       outline: "Home" },
  { id: "experience", label: "experience.log", outline: "Experience" },
  { id: "projects", label: "projects.json",   outline: "Work" },
  { id: "about",    label: "about.md",        outline: "About me" },
  { id: "skills",   label: "stack.config.js", outline: "Tech stack" },
  { id: "certs",    label: "education.yml",   outline: "Credentials" },
  { id: "contact",  label: "contact.ts",      outline: "Contact" },
];

// ─── Skills ───────────────────────────────────────────────────────────────────
export const SKILLS = {
  languages: ["Java", "Python", "TypeScript", "JavaScript", "C", "C#", ".NET"],
  web:       ["React", "Next.js", "Node.js", "Angular", "FastAPI"],
  cloud:     ["AWS Lambda", "S3", "Cognito", "API Gateway", "AWS CDK", "Amplify", "Vercel", "Railway", "Docker", "GitHub Actions"],
  ml:        ["LightGBM", "scikit-learn", "pandas", "NumPy"],
  databases: ["PostgreSQL", "MySQL", "MongoDB", "Redis"],
  tools:     ["Git", "Linux", "Postman", "VS Code", "IntelliJ"],
};

// ─── Projects ─────────────────────────────────────────────────────────────────
export const PROJECTS = [
  {
    title:  "TransitIQ",
    kind:   "Full-stack · Machine learning",
    desc:   "Predicts TTC delays before they happen instead of reporting them after. A LightGBM classifier trained on 270,637 historical transit events plus live weather reaches 0.91 ROC-AUC, and pushes a browser notification when a saved route crosses 70% delay probability. Five services behind it: React PWA, Node API, FastAPI model server, Postgres, and a Redis stream decoupling the GTFS-RT collector from the writer.",
    tech:   ["React", "Node.js", "FastAPI", "LightGBM", "PostgreSQL"],
    glyph:  "◉",
    href:   "https://transitiq.vercel.app/",
  },
  {
    title:  "Morphix",
    kind:   "Web · AWS Serverless",
    desc:   "A serverless image converter with no servers to manage. Next.js on Amplify, a Cognito JWT authorizer on every route, and two Python Lambdas — one presigns an S3 upload the browser writes to directly, the other runs Pillow and hands back a download URL. Every resource is CDK: one deploy provisions S3, Lambda, API Gateway, Cognito and IAM.",
    tech:   ["Next.js", "TypeScript", "Python", "AWS Lambda", "CDK"],
    glyph:  "⇄",
    href:   "https://main.d3079iqimuc5os.amplifyapp.com/",
  },
  {
    title:  "Blackjack Game",
    kind:   "Desktop / Java",
    desc:   "A complete Blackjack implementation built around clean card-game architecture — encapsulated hand and deck models, an inheritance-driven player hierarchy, and polymorphic turn resolution.",
    tech:   ["Java", "OOP", "Data Structures"],
    glyph:  "♠",
    href:   "https://github.com/nisarg0330",
  },
  {
    title:  "Portfolio v2",
    kind:   "Web / React",
    desc:   "This site. An editor-themed single page built on React with no UI framework — hand-written CSS tokens, an IntersectionObserver-driven outline, and a line-number gutter that tracks the viewport.",
    tech:   ["React", "CSS", "a11y"],
    glyph:  "◧",
    href:   "https://www.nisargp.com/",
  },
];

// ─── Experience ───────────────────────────────────────────────────────────────
export const EXPERIENCE = [
  {
    role:     "Software Engineer",
    company:  "Manulife",
    location: "ON, Canada",
    period:   "Oct 2025 — present",
    points: [
      "Designed and implemented microservices-based enterprise applications using Java 11, Spring Boot, and Spring Cloud.",
      "Developed and maintained modular services for policy, claims, and account management.",
      "Built Angular front-end components for customer onboarding, premium tracking, and policy renewal workflows.",
      "Integrated RESTful APIs between front-end and backend microservices to ensure smooth data flow.",
      "Implemented secure authentication and authorization using OAuth2, JWT, and SSO frameworks.",
      "Developed payment gateway integrations supporting credit card, auto-debit, and third-party payment providers.",
      "Utilized Kafka and RabbitMQ for real-time, event-driven communication across distributed microservices.",
      "Designed database schemas and stored procedures in Oracle, MySQL, and MongoDB for transactional data.",
      "Ensured code quality and coverage by applying TDD practices using JUnit, Mockito, and Jasmine.",
      "Automated build and deployment pipelines using Git, Jenkins, Docker, and Kubernetes for CI/CD.",
      "Collaborated closely with business analysts and QA teams to translate requirements into technical solutions.",
      "Followed Agile/Scrum methodology, participating in sprint planning, code reviews, and daily stand-ups.",
      "Applied SOLID principles and clean code practices to deliver scalable, maintainable, and high-performance applications.",
    ],
    tech: ["Java 11", "Spring Boot", "Spring Cloud", "Angular", "Kafka", "RabbitMQ", "Oracle", "MongoDB", "Docker", "Kubernetes", "Jenkins"],
  },
];

// ─── Focus areas ──────────────────────────────────────────────────────────────
export const FOCUS = [
  {
    n: "01",
    title: "Backend & APIs",
    items: ["REST services in Node and FastAPI", "Relational and document data modelling", "Auth, validation, error handling"],
  },
  {
    n: "02",
    title: "Frontend",
    items: ["React with hooks and composition", "Responsive layout in Grid and Flexbox", "Accessible, keyboard-navigable UI"],
  },
  {
    n: "03",
    title: "Cloud & delivery",
    items: ["AWS core services (EC2, S3, RDS, IAM)", "Containerised builds with Docker", "CI pipelines in GitHub Actions"],
  },
];

// ─── Timeline ─────────────────────────────────────────────────────────────────
export const TIMELINE = [
  { year: "2025 — now", title: "Software Engineer", place: "Manulife" },
  { year: "2023 — 2025", title: "Computer Programming Diploma", place: "Sheridan College" },
  { year: "2024",        title: "Cloud fundamentals coursework",  place: "AWS Skill Builder" },
];

// ─── Credentials ──────────────────────────────────────────────────────────────
export const CERTIFICATIONS = [
  {
    provider: "Sheridan College",
    title:    "Computer Programming Diploma",
    year:     "2025",
    status:   "completed",
    desc:     "Graduated with distinction. Coursework across software development, data structures, algorithms, and system design.",
  },
  {
    provider: "Amazon Web Services",
    title:    "AWS Certified Cloud Practitioner",
    year:     "2026",
    status:   "in progress",
    desc:     "Foundational AWS cloud services, architecture, security, pricing, and support — EC2, S3, RDS, and IAM.",
  },
];

// ─── Stats ────────────────────────────────────────────────────────────────────
export const STATS = [
  ["04", "projects shipped"],
  ["22", "technologies used"],
  ["01", "certification in progress"],
];
